import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
const ACTIVE_ORG_KEY = "amcal_active_org";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeOrgId, setActiveOrgIdState] = useState(() => localStorage.getItem(ACTIVE_ORG_KEY) || null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      const memberships = data?.memberships || [];
      // Auto-pick active org if missing or stale
      const stored = localStorage.getItem(ACTIVE_ORG_KEY);
      const validIds = memberships.map(m => m.org_id);
      if (memberships.length > 0 && !validIds.includes(stored)) {
        setActiveOrgIdState(memberships[0].org_id);
        localStorage.setItem(ACTIVE_ORG_KEY, memberships[0].org_id);
      }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const setActiveOrg = useCallback((orgId) => {
    setActiveOrgIdState(orgId);
    if (orgId) localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    else localStorage.removeItem(ACTIVE_ORG_KEY);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      // Re-fetch /auth/me to get memberships
      await checkAuth();
      return { success: true, user: data };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    setUser(false);
    setActiveOrg(null);
  };

  const activeMembership = useMemo(() => {
    if (!user || !user.memberships) return null;
    return user.memberships.find(m => m.org_id === activeOrgId) || user.memberships[0] || null;
  }, [user, activeOrgId]);

  const activeOrg = activeMembership?.org || null;
  const isOrgAdmin = activeMembership?.role === "org_admin";

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, activeOrgId: activeOrg?.id || null, activeOrg, activeMembership, isOrgAdmin, setActiveOrg }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
